/**
 * Elven roster — UNIT_ART indices 12..16, drawn at the doubled tile resolution.
 *
 * **The racial proportion system.** A player has to tell the three peoples apart
 * as shapes, before colour, at the size a unit actually occupies on screen. The
 * set therefore holds to one rule across all three rosters:
 *
 *              head-top row   body core width   leg rows
 *   Dwarf          4-5              10              1-2
 *   Human          1-4               6                3
 *   Elf              0                4                5
 *
 * All three still plant their feet on row 15, so they share a ground line. An
 * elf is the tallest thing in the set and the narrowest; every elf gets its
 * visual mass back from an accessory that is *not* the body — the Longbow's
 * stave, the Druid's antlers and hem, the Shadowdancer's low-slung cloak, the
 * Blademaster's two blades. Thin core, wide gesture. That is the whole read.
 */
export const ART_UNITS_ELF: string[][][] = [
  /* 12 Blademaster — 16x16. Crown of the skull on row 0, so he stands a full head
     over a Knight; core four pixels wide against the Knight's six and the
     dwarves' ten. Silver hair under a leaf circlet, twin curved sabres in an
     off-balance guard — one blade high, one low, and they swap on the beat. */
  [
    [
      '      gNNg      ',
      '      NkkN   m  ',
      '      Nss   m   ',
      '     sPPPPsm    ',
      '     qPPPPq     ',
      '      PggP      ',
      '     sPPPPs     ',
      '    m pPPp      ',
      '   m  pPPp      ',
      '  m   qqqq      ',
      '      pPPp      ',
      '      q  q      ',
      '      q  q      ',
      '      q  q      ',
      '      q  q      ',
      '      #  #      ',
    ],
    [
      '      gNNg      ',
      '  m   NkkN      ',
      '   m  Nss       ',
      '    msPPPPs     ',
      '     qPPPPq     ',
      '      PggP      ',
      '     sPPPPs     ',
      '      pPPp m    ',
      '      pPPp  m   ',
      '      qqqq   m  ',
      '      pPPp      ',
      '     q    q     ',
      '     q    q     ',
      '    q      q    ',
      '    q      q    ',
      '    #      #    ',
    ],
  ],

  /* 13 Longbow — 16x16. The bow runs the full sixteen rows, taller than the elf
     who carries it and half again the human Archer's stave: all recurve, no
     straight line. Body on the elven four-pixel core, leaf circlet, legs a third
     of her height. Frame 1 thrusts the bow out and nocks an arrow on the rest. */
  [
    [
      '     w   gNNg   ',
      '    w    NkkN   ',
      '   w      Nss   ',
      '   w    sPPPPs  ',
      '   w    qPPPPq  ',
      '   w     PggP   ',
      '   wssss PPPP   ',
      '   w     pPPp   ',
      '   w     qqqq   ',
      '   w     pPPp   ',
      '   w     q  q   ',
      '    w    q  q   ',
      '    w    q  q   ',
      '     w   q  q   ',
      '     w   q  q   ',
      '      w  #  #   ',
    ],
    [
      '    w    gNNg   ',
      '   w     NkkN   ',
      '  w       Nss   ',
      '  w     sPPPPs  ',
      '  w     qPPPPq  ',
      '  w      PggP   ',
      ' mwwwwwwsPPPP   ',
      '  w      pPPp   ',
      '  w      qqqq   ',
      '  w      pPPp   ',
      '  w      q  q   ',
      '   w     q  q   ',
      '   w    q    q  ',
      '    w   q    q  ',
      '    w  q      q ',
      '     w #      # ',
    ],
  ],

  /* 14 Druid — 16x16. Deliberately the inverse of the human Wizard's cone: the
     Wizard is a point on top over a ten-wide hem, the Druid is a nine-wide
     antler spread over a narrow eight-wide robe. Living-wood stave with a
     leaf-wrapped moonstone. The robe reaches the floor, so there are no feet
     under it. Frame 1 brightens the stave, sways the robe and raises the free
     hand with a spark in it. */
  [
    [
      '   g       g    ',
      '    w     w gcg ',
      '     w   w   w  ',
      '      gNNg   w  ',
      '      NkkN   w  ',
      '      Nss    w  ',
      '     gPPPPg  w  ',
      '     sPPPPs  w  ',
      '      PPPP   w  ',
      '      qqqq   w  ',
      '     pPPPPp  w  ',
      '     pPPPPp  w  ',
      '     pPPPPp  w  ',
      '    pPPPPPPp w  ',
      '    pPPPPPPp w  ',
      '   G########Gw  ',
    ],
    [
      '   g       g    ',
      '    w     w gcc ',
      '     w   w   w  ',
      '      gNNg   w  ',
      '      NkkN   w  ',
      '      Nss    w  ',
      '   c gPPPPg  w  ',
      '    ssPPPPs  w  ',
      '      PPPP   w  ',
      '      qqqq   w  ',
      '      pPPPPp w  ',
      '      pPPPPp w  ',
      '      pPPPPp w  ',
      '     pPPPPPPpw  ',
      '     pPPPPPPpw  ',
      '    G#######Gw  ',
    ],
  ],

  /* 15 Shadowdancer — 16x16. The one sprite most at risk of reading as the human
     Thief, so she is drawn as far from him as the palette allows: a head taller,
     a four-pixel core against his five, legs running a third of her height, and
     the cloak kept off the shoulders so the bulk sits low. Deep hood with two
     arcane eyes, a dark-leaf clasp instead of a buckle, a knife in each hand.
     Frame 1 is a full lunge: the blades trade height and the cloak streams off
     the back foot. */
  [
    [
      '      oooo      ',
      '     oovvoo     ',
      '      oooo  m   ',
      '     oPPPPom    ',
      '     oPPPPo     ',
      '      PGGP      ',
      '      PPPP      ',
      '    moPPPPo     ',
      '   m  qqqq      ',
      '     opPPpo     ',
      '    oopPPpoo    ',
      '   oo q  q oo   ',
      '   o  q  q  o   ',
      '      q  q      ',
      '      q  q      ',
      '      #  #      ',
    ],
    [
      '      oooo      ',
      '     oovvoo     ',
      '   m  oooo      ',
      '    moPPPPo     ',
      '     oPPPPo     ',
      '      PGGP  m   ',
      '      PPPPom    ',
      '     oPPPPo     ',
      '      qqqq      ',
      '     opPPpo     ',
      '   ooopPPpo     ',
      '  oo q    q     ',
      '  o  q    q     ',
      '    q      q    ',
      '    q      q    ',
      '    #      #    ',
    ],
  ],

  /* 16 Phoenix — 32x20. A bird made of fire: hooked beak, black eye, a mantle of
     kingdom colour across the back and shoulders, and three burning tail
     ribbons that trail away behind it to the bottom of the frame. Two frames,
     because it is on fire and flying: frame 0 has the wings raised to their
     tips, frame 1 catches the downstroke with the wings spread wide and low and
     the tail snapped up behind. */
  [
    [
      '                                ',
      '                                ',
      '   rr                      rr   ',
      '  ryr                      ryr  ',
      '  ryyr                    ryyr  ',
      '  ryyyr                  ryyyr  ',
      '   ryyyr                ryyyr   ',
      '    ryyyr              ryyyr    ',
      '     ryyyyr          ryyyyr     ',
      '       ryyyyr      ryyyyr       ',
      '         ryyyy    yyyyr         ',
      '           rPPPPPPPPr           ',
      '          rPPPPPPPPyyyyykyy     ',
      '         rpPPPPPPPPpyyyyyrr     ',
      '       ryyrpPPPPPPpyyyyr        ',
      '    ryyr ryypPPPPPpyr           ',
      '  ryyr  ryyyypPPPPPy            ',
      ' ryyr   ryyyr yy yy             ',
      '   ryyr  ryyr ## ##             ',
      '     ryyr  ryr                  ',
    ],
    [
      '                                ',
      '                                ',
      '                                ',
      '                                ',
      '                                ',
      '                                ',
      '   rr                      rr   ',
      '  ryyyr                  ryyyr  ',
      ' ryyyyyr                ryyyyyr ',
      '  ryyyyyyr            ryyyyyyr  ',
      '    ryyyyyyyy      yyyyyyyyr    ',
      '       ryyyrPPPPPPPPryyyr       ',
      '          rPPPPPPPPyyyyykyy     ',
      '         rpPPPPPPPPpyyyyyrr     ',
      '      ryyyrpPPPPPPpyyyyr        ',
      '   ryyr ryyypPPPPPpyr           ',
      ' ryyr  ryyyyypPPPPPy            ',
      '  ryyr   ryyr ## ##             ',
      '    ryyr  ryr                   ',
      '      ryr                       ',
    ],
  ],
];
